import { Box, Button, Text } from '@chakra-ui/react';
import React, { useState } from 'react';
import { CustomFieldValue } from '../../types';
import FormattedText from '../rich-text-editor/FormattedText';

interface RichTextCustomFieldCardProps {
  title: string;
  customField?: CustomFieldValue[] | undefined;
  backgroundColor: string;
  gradientColor: string;
  buttonColor: string;
  buttonHoverColor: string;
  emptyStateMessage: string;
  alwaysExpanded?: boolean;
}

export const RichTextCustomFieldCard: React.FC<
  RichTextCustomFieldCardProps
> = ({
  title,
  customField,
  backgroundColor,
  gradientColor,
  buttonColor,
  buttonHoverColor,
  emptyStateMessage,
  alwaysExpanded = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const effectivelyExpanded = alwaysExpanded || isExpanded;

  // Check if field has content
  const hasContent =
    customField &&
    Array.isArray(customField) &&
    customField.length > 0;

  if (hasContent) {
    const latestField = customField[customField.length - 1];
    const content =
      latestField?.metadata?.htmlContent || latestField?.value || '';

    // Check if content is actually empty after trimming
    if (
      !content ||
      (typeof content === 'string' && content.trim() === '')
    ) {
      return (
        <>
          <Text
            fontSize="0.875rem"
            fontWeight="700"
            lineHeight="1.25rem"
            color="text.secondary"
            mb="0.5rem"
          >
            {title}
          </Text>
          <Text
            fontSize="sm"
            color="text.secondary"
            fontStyle="italic"
          >
            {emptyStateMessage}
          </Text>
        </>
      );
    }

    return (
      <>
        <Text
          fontSize="0.875rem"
          fontWeight="700"
          lineHeight="1.25rem"
          color="text.secondary"
          mb="0.5rem"
        >
          {title}
        </Text>
        <Box
          borderRadius="0.5rem"
          bg={backgroundColor}
          display="flex"
          padding="0.75rem 1rem"
          alignItems="flex-start"
          gap="0.75rem"
          overflow="hidden"
          minHeight="3rem"
          position="relative"
        >
          <Box
            maxHeight={effectivelyExpanded ? 'none' : '6rem'}
            overflow={effectivelyExpanded ? 'visible' : 'auto'}
            flex="1"
            transition="max-height 0.3s ease-in-out"
            css={{
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#cbd5e0',
                borderRadius: '2px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#a0aec0',
              },
            }}
          >
            <FormattedText
              content={String(content)}
              fontSize="0.875rem"
              lineHeight="1.6"
              spacing="0.75rem"
              maxHeight="none"
              overflow="visible"
            />
          </Box>

          {/* Fade gradient overlay for collapsed state */}
          <Box
            position="absolute"
            bottom="0"
            left="0"
            right="0"
            height="2rem"
            background={`linear-gradient(transparent, ${gradientColor})`}
            pointerEvents="none"
            zIndex={1}
            display={
              content &&
              typeof content === 'string' &&
              content.trim() !== '' &&
              content.length > 400 &&
              !effectivelyExpanded
                ? 'block'
                : 'none'
            }
          />
        </Box>

        {/* Show More/Less button for long content */}
        {!alwaysExpanded &&
          content &&
          typeof content === 'string' &&
          content.trim() !== '' &&
          content.length > 400 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsExpanded(!isExpanded)}
              mt="0.5rem"
              fontSize="0.8rem"
              color={buttonColor}
              _hover={{ bg: buttonHoverColor }}
            >
              {isExpanded ? 'Show Less' : 'Show More'}
            </Button>
          )}
      </>
    );
  }

  // If no content at all, don't show anything
  return null;
};

export default RichTextCustomFieldCard;
